
import React from 'react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { Panel, Label, Stack, Dropdown, PrimaryButton, Toggle, IDropdownOption, PanelType, MessageBar, MessageBarType, Spinner, SpinnerSize } from "@fluentui/react";
import { ITenantUser, MollieSubscription } from '../../data-structures/interfaces';
import { labelHeadingStyle, dropdownStyles, labelInfoStyle } from "../../styles/PanelStyle";
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../redux';
import { updateUser } from '../../redux/modules/user';
import { LandingPage, SubscriptionStatusEnum } from '../../data-structures/enums';
import { assignUsersToSubscription, getTenantSubscriptions, revokeUsersFromSubscription } from '../../utilities/helpers/ApiHelper';
import { getUserLicenseType } from '../../utilities/helpers/UserLicenseTypeHelper';
import { canAssignUsersToSubscription, isSubscriptionAssignable } from '../../utilities/helpers/SubscriptionCapacityHelper';

const revokeSubscriptionKey = "revokeUser";

const mapStateToProps = (state: RootState) => {
    return {
        isPartner: state.user.isPartner,
        isGlobalAdmin: state.user.isGlobalAdmin,
        isTenant: state.user.isTenantAdmin,
        tenants: state.user.tenants
    };
};

const mapDispatchToProps = {
    updateUser
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface EditPanelProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void,
    tenantUser: ITenantUser,
};

const EditPanel: React.FC<EditPanelProps> = ({
    isOpen,
    closePanel,
    tenantUser,
    isPartner,
    isGlobalAdmin,
    updateUser,
    tenants
}: EditPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [newTenantUser, setNewTenantUser] = React.useState<ITenantUser>({ ...tenantUser })
    const [originalTenantUser, setOriginalTenantUser] = React.useState<ITenantUser>({ ...tenantUser });
    const [isSaveDisabled, setIsSaveDisabled] = React.useState<boolean>(true);
    const [subscriptions, setSubscriptions] = React.useState<MollieSubscription[]>([]);
    const [subscriptionOptions, setSubscriptionOptions] = React.useState<IDropdownOption[]>([]);
    const [isSubscriptionOptionsLoaded, setIsSubscriptionOptionsLoaded] = React.useState<boolean>(false);
    const [selectedSubscriptionKey, setSelectedSubscriptionKey] = React.useState<string | number>();
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();

    const getNotEnoughLicensesMessage = React.useCallback((needed: number, available: number) => {
        return languageStrings.NotEnoughLicensesAvailable
            .replace("{needed}", needed.toString())
            .replace("{available}", available.toString());
    }, [languageStrings]);

    const getAssignmentValidationError = React.useCallback((subscription: MollieSubscription | undefined, requiredLicenseCount: number): string => {
        if (requiredLicenseCount <= 0) {
            return "";
        }

        if (!isSubscriptionAssignable(subscription)) {
            return languageStrings.SubscriptionUnavailable;
        }

        if (!canAssignUsersToSubscription(subscription, requiredLicenseCount)) {
            return getNotEnoughLicensesMessage(requiredLicenseCount, subscription?.availableLicenseCount ?? 0);
        }

        return "";
    }, [getNotEnoughLicensesMessage, languageStrings]);

    React.useEffect(() => {
        if (!isOpen) {
            setSubscriptions([]);
            setSubscriptionOptions([]);
            setIsSubscriptionOptionsLoaded(false);
            return;
        }

        let isCurrent = true;
        setSubscriptions([]);
        setSubscriptionOptions([]);
        setIsSubscriptionOptionsLoaded(false);

        (async () => {
            if (!(tenantUser && tenants?.length > 0)) {
                if (isCurrent) {
                    setIsSubscriptionOptionsLoaded(true);
                }
                return;
            }

            const tenant = tenants.find(x => x.tenantId === tenantUser.tenantId);
            if (!tenant) {
                if (isCurrent) {
                    setIsSubscriptionOptionsLoaded(true);
                }
                return;
            }

            try {
                const subscriptions = await getTenantSubscriptions(tenant.id, languageStrings);

                if (!isCurrent) {
                    return;
                }

                setSubscriptions(subscriptions);

                const tenantSubs: IDropdownOption[] = subscriptions
                    .filter(x => {

                        if (tenantUser?.subscriptionId && x.id === tenantUser.subscriptionId) {
                            return true;
                        }

                        return isSubscriptionAssignable(x);
                    })
                    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                    .map(sub => {
                        let licenseText: string;
                        const status: string = (sub.status as string)?.toLowerCase();

                        // if (status === SubscriptionStatusEnum.Canceled) {
                        //     licenseText = `(${languageStrings.Canceled})`;
                        // } else if (sub.method === "invoice" || sub.method === "invoice_static" || sub.method === "invoice_dynamic") {
                        //     licenseText = `(${languageStrings.Invoice})`;
                        // } else if (sub.tier === SubscriptionTierEnum.Trial) {
                        //     licenseText = `(${new Date(sub.expiryDate).toLocaleDateString()})`;
                        // } else {
                        //     licenseText = `(${sub.availableLicenses} ${languageStrings.Available.toLocaleLowerCase()})`;
                        // }

                        if (
                            status === SubscriptionStatusEnum.Suspended
                            || status === SubscriptionStatusEnum.Completed
                            || (status === SubscriptionStatusEnum.Canceled && (!sub.expiryDateRaw || sub.expiryDateRaw < new Date()))
                        ) {
                            licenseText = `(${languageStrings.Inactive})`;
                        } else {
                            licenseText = `(${sub.availableLicenses !== "-" ? sub.availableLicenses : languageStrings.Unlimited} ${languageStrings.Available.toLocaleLowerCase()})`;
                        }

                        if (sub.interval) {
                            licenseText += sub.interval === languageStrings.OnceOff
                                ? " - Trial"
                                : ` - ${sub.interval}`;
                        }

                        const option: IDropdownOption = {
                            key: sub.id,
                            text: `${sub.product} ${licenseText}`,
                            data: sub,
                            disabled: !canAssignUsersToSubscription(sub, tenantUser?.subscriptionId === sub.id ? 0 : 1)
                        }

                        return option;
                    });

                setSubscriptionOptions(tenantSubs);
            } catch (error) {
                console.error(error);
            } finally {
                if (isCurrent) {
                    setIsSubscriptionOptionsLoaded(true);
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
    }, [isOpen, languageStrings, tenantUser, tenants])

    React.useEffect(() => {
        setOriginalTenantUser({ ...tenantUser })
        setNewTenantUser({ ...tenantUser })
        setSelectedSubscriptionKey(tenantUser?.subscriptionId ?? undefined);
        setErrorMessage(undefined);
    }, [isOpen, tenantUser])

    React.useEffect(() => {
        isDirty()
    }, [newTenantUser])

    const landingPageOptions = [
        { key: LandingPage.Default, text: languageStrings.Default },
        { key: LandingPage.SharePoint, text: "SharePoint" },
        { key: LandingPage.Teams, text: "Teams" },
        { key: LandingPage.OneDrive, text: "OneDrive" },
        { key: LandingPage.Exchange, text: "Exchange" }
    ]

    const subscriptionDropdownOptions = React.useMemo<IDropdownOption[]>(() => {
        const canRevokeSubscription = !!originalTenantUser?.subscriptionId
            || !!newTenantUser?.subscriptionId
            || selectedSubscriptionKey === revokeSubscriptionKey;

        if (!canRevokeSubscription) {
            return subscriptionOptions;
        }

        return [
            {
                key: revokeSubscriptionKey,
                text: languageStrings.RevokeLicenses,
            },
            ...subscriptionOptions
        ];
    }, [languageStrings.RevokeLicenses, newTenantUser?.subscriptionId, originalTenantUser?.subscriptionId, selectedSubscriptionKey, subscriptionOptions]);

    const isDirty = () => {
        let enableSave: boolean = false;
        const userKeys = Object.keys(newTenantUser);
        userKeys.every(key => {
            if (key === "key") return true //skip key check, bc we get a new key on rerender 
            else {
                if (typeof newTenantUser[key] != "object") { //if it is not an array or object, do this code
                    if (newTenantUser[key] != originalTenantUser[key]) {
                        enableSave = true;
                        return false;
                    } else {
                        return true
                    }
                } else {
                    if (key === "licenseExpiryDate" && new Date(newTenantUser[key]).toDateString() != new Date(originalTenantUser[key]).toDateString()) {
                        enableSave = true;
                        return false
                    } else {
                        return true
                    }
                }
            }
        })
        setIsSaveDisabled(!enableSave);
    }

    const isActivatedEnabled = () => {
        if (originalTenantUser.isActivated === false) {
            const userSubscription = subscriptions.find(sub => sub.id === newTenantUser.subscriptionId);
            if (userSubscription) {
                const requiredLicenseCount = originalTenantUser.subscriptionId === userSubscription.id ? 0 : 1;
                if (!isSubscriptionAssignable(userSubscription)
                    || !canAssignUsersToSubscription(userSubscription, requiredLicenseCount)) {
                    return false;
                }
            }
        }
        return true;
    }

    const onSubscriptionChange = (_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (!item) {
            return;
        }

        setSelectedSubscriptionKey(item.key);
        setErrorMessage(undefined);

        if (item.key === revokeSubscriptionKey) {
            setNewTenantUser({
                ...newTenantUser,
                subscriptionId: null,
                licenseType: getUserLicenseType(null),
                productType: null
            });
            return;
        }

        const subscriptionId = Number(item.key);
        if (Number.isNaN(subscriptionId)) {
            return;
        }

        const subscription = item.data ?? subscriptions.find(sub => sub.id === subscriptionId);
        setNewTenantUser({
            ...newTenantUser,
            subscriptionId: subscriptionId,
            licenseType: getUserLicenseType(subscription),
            productType: subscription?.product?.trim() ?? null
        });
    };

    const saveChanges = async () => {
        setIsSaving(true);
        setErrorMessage(undefined);

        try {
            let userToSave = { ...newTenantUser };
            const subscriptionChanged = newTenantUser.subscriptionId !== originalTenantUser.subscriptionId;

            if (subscriptionChanged) {
                const tenant = tenants.find(x => x.tenantId === tenantUser.tenantId);
                if (!tenant) {
                    setErrorMessage(languageStrings.NoTenantSelected);
                    return;
                }

                if (!newTenantUser.subscriptionId) {
                    const revokeResult = await revokeUsersFromSubscription({
                        tenantId: tenant.id,
                        subscriptionId: null,
                        userIds: [tenantUser.id]
                    });
                    if (revokeResult === null || revokeResult === undefined) {
                        setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                        return;
                    }
                } else {
                    const currentSubscriptions = await getTenantSubscriptions(tenant.id, languageStrings);
                    setSubscriptions(currentSubscriptions);

                    const subscription = currentSubscriptions.find(sub => sub.id === newTenantUser.subscriptionId);
                    const validationError = getAssignmentValidationError(subscription, 1);
                    if (validationError) {
                        setErrorMessage(validationError);
                        return;
                    }

                    const updatedUserIds = await assignUsersToSubscription({
                        tenantId: tenant.id,
                        subscriptionId: subscription.id,
                        userIds: [tenantUser.id]
                    });

                    if (!Array.isArray(updatedUserIds) || !updatedUserIds.includes(tenantUser.id)) {
                        setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                        return;
                    }

                    userToSave = {
                        ...userToSave,
                        subscriptionId: subscription.id,
                        licenseType: getUserLicenseType(subscription),
                        productType: subscription.product?.trim() ?? null
                    };
                }
            }

            updateUser(userToSave);
            closePanel();
        } catch (error) {
            console.error(error);
            setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Panel
            headerText="Edit user"
            isOpen={isOpen}
            isLightDismiss
            onDismiss={closePanel}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={"500px"}
        >
            <Stack tokens={{ childrenGap: 15 }}>
                <Stack.Item style={{ marginTop: 15 }}>
                    <Label styles={labelHeadingStyle}>{languageStrings.Username}</Label>
                    <Label styles={labelInfoStyle} disabled>{tenantUser?.username}</Label>
                </Stack.Item>
                <Stack.Item>
                    <Label styles={labelHeadingStyle}>{languageStrings.DisplayName}</Label>
                    <Label styles={labelInfoStyle} disabled>{tenantUser?.displayName}</Label>
                </Stack.Item>
                <Stack.Item>
                    <Label styles={labelHeadingStyle}>{languageStrings.Subscription}</Label>
                    {(subscriptionOptions?.length > 0 || (isSubscriptionOptionsLoaded && subscriptionDropdownOptions?.length > 0)) && <Dropdown
                        placeholder={languageStrings.SelectSubscription}
                        options={subscriptionDropdownOptions}
                        onChange={onSubscriptionChange}
                        selectedKey={selectedSubscriptionKey}
                    />}
                </Stack.Item>
                {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.Active}</Label>}
                        checked={newTenantUser.isActivated}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isActivated: checked ?? false })}
                        disabled={!isActivatedEnabled()}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.TenantAdmin}</Label>}
                        checked={newTenantUser.isTenantAdmin}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isTenantAdmin: checked ?? false })}
                        disabled={!(isPartner || isGlobalAdmin)}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.Partner}</Label>}
                        checked={newTenantUser.isPartner}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isPartner: checked ?? false })}
                        disabled={true}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item >
                    <Label styles={labelHeadingStyle}>{languageStrings.GeneralSettings}</Label>
                    <Toggle
                        label={languageStrings.Show + " SharePoint"}
                        checked={newTenantUser.enableSharePoint}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableSharePoint: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.Show + " Teams"}
                        checked={newTenantUser.enableTeams}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableTeams: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.Show + " OneDrive"}
                        checked={newTenantUser.enableOneDrive}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableOneDrive: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.EnableApprovals}
                        checked={newTenantUser.enableApprovals}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableApprovals: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Stack.Item>
                        <Label styles={labelHeadingStyle}>{languageStrings.LandingPage}</Label>
                        <Dropdown
                            style={{ marginTop: 7 }}
                            styles={dropdownStyles}
                            selectedKey={newTenantUser?.landingPage ? newTenantUser?.landingPage?.valueOf() : landingPageOptions[0].key}
                            onChange={(_e, o) => setNewTenantUser({ ...newTenantUser, landingPage: Number(o.key) })}
                            options={landingPageOptions}
                        />
                    </Stack.Item>
                </Stack.Item>

                <Stack.Item>
                    <Label styles={labelHeadingStyle}>
                        {languageStrings.OutlookSettings}
                    </Label>
                    <Toggle
                        disabled={true}
                        label={languageStrings.SaveOnSend}
                        checked={newTenantUser.saveOnSend ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, saveOnSend: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.AutoSelectAttachments}
                        checked={newTenantUser.autoSelectAttachments ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, autoSelectAttachments: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.AutoRemoveMailItemAttachments}
                        checked={newTenantUser.autoRemoveMailItemAttachments ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, autoRemoveMailItemAttachments: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.ConvertEmailToPdf}
                        checked={newTenantUser.convertEmailToPDF ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, convertEmailToPDF: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack horizontal horizontalAlign="end">
                    <PrimaryButton
                        disabled={isSaveDisabled || isSaving}
                        iconProps={{ iconName: 'save' }}
                        onClick={saveChanges}>
                        {languageStrings.Save}
                        {isSaving && <Spinner size={SpinnerSize.small} style={{ paddingLeft: 10 }} />}
                    </PrimaryButton>
                </Stack>

            </Stack>

        </Panel>
    )
}

export default connector(EditPanel);
